<template>
  <v-container>
    <v-responsive
      class="align-center mx-auto"
      max-width="1000"
    >
      <h1 class="text-h4 font-weight-black pt-8 pb-4 pl-3">
        Welcome to {{ brand.productName }}
      </h1>


      <template
        v-for="shortcut in shortcuts"
        :key="shortcut.title"
      >
        <div class="py-4">
          <h2 class="text-h6 font-weight-bold ms-3 mb-4 text-primary">
            {{ shortcut.title }}
          </h2>
          
          <v-row>
            <v-col
              v-for="item in shortcut.items"
              :key="item.title"
              cols="12"
              md="6"
              class="py-2"
            >
              <v-card
                :link="true"
                :href="item.external ? (typeof item.route === 'string' ? item.route : undefined) : undefined"
                :to="!item.external ? (item.route as RouteLocationRaw) : undefined"
                :target="item.external ? '_blank' : undefined"
                variant="flat"
                class="h-100"
                elevation="0"
                :class="{ 'border': !item.external, 'bg-grey-lighten-4': item.external }"
              >
                <v-card-item>
                  <template #prepend>
                    <v-avatar
                      :color="item.external ? 'grey-lighten-2' : 'primary-lighten-5'"
                      size="48"
                      rounded
                    >
                      <v-icon
                        :icon="item.icon"
                        :color="item.external ? 'grey-darken-2' : 'primary'"
                        size="24"
                      />
                    </v-avatar>
                  </template>

                  <v-card-title class="font-weight-bold ps-4 text-truncate">
                    {{ item.title }}
                    <v-icon
                      v-if="item.external"
                      icon="mdi-open-in-new"
                      size="16"
                      class="ms-2"
                    />
                  </v-card-title>
                  <v-card-subtitle class="ps-4 pt-1 text-wrap">
                    {{ item.subtitle }}
                  </v-card-subtitle>
                </v-card-item>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </template>
    </v-responsive>

    <OnboardingWizard v-if="showOnboarding" />
  </v-container>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue'

import { brand } from '@/config/brand'
import { portal, FEATURE_CATALOG } from '@/config/portal'
import { useAuthStore } from '@/stores/auth'
import type { RouteLocationRaw } from 'vue-router'
import OnboardingWizard from '@/components/onboarding/OnboardingWizard.vue'

const authStore = useAuthStore()
const showOnboarding = ref(false)

onMounted(async () => {
  let user = authStore.getUser
  if (!user) {
    await authStore.fetchUser()
    user = authStore.getUser
  }
  
  // Show onboarding if either:
  // 1. No profile exists
  // 2. Profile exists but required documents not signed
  // 3. There is an active click-through agreement (SD-19-03/19-05) the user
  //    has not yet accepted — presence-driven, no brand key involved.
  const hasUnacceptedAgreement = !!(
    user?.click_through_agreement?.required && !user?.click_through_agreement?.accepted
  )
  showOnboarding.value = user
    ? (!user.profile || !user.organization?.signed_required_documents || hasUnacceptedAgreement)
    : false
})

// Card groups are derived from the active portal config (SD-2-01), not a
// hardcoded literal. Each card maps to the template's existing item contract
// (`{ title, subtitle, icon, route, external }`) so the v-for needs no markup
// change. Internal cards (with a `feature`) resolve route/icon/title via
// FEATURE_CATALOG, with per-card overrides; cards for features not enabled by
// the portal are filtered out. External cards (with an `href`) link out.
// Portal config is fixed per build (singleton), so this view model is built
// once at setup rather than wrapped in a computed.
const enabledFeatures = new Set(portal.features)

const shortcuts = portal.home.map((group) => ({
  title: group.title,
  items: group.cards
    .filter((card) =>
      // Drop internal cards whose feature this portal does not enable.
      card.feature ? enabledFeatures.has(card.feature) : true,
    )
    .map((card) => {
      if (card.feature) {
        const catalog = FEATURE_CATALOG[card.feature]
        return {
          title: card.title ?? catalog.defaultLabel,
          subtitle: card.subtitle,
          icon: card.icon ?? catalog.icon,
          route: { name: catalog.routeName },
          external: false,
        }
      }
      return {
        title: card.title,
        subtitle: card.subtitle,
        icon: card.icon,
        route: card.href,
        external: true,
      }
    }),
}))
</script>

<style scoped>
.search-field {
  :deep(.v-field__input) {
    min-height: 56px !important;
    padding: 12px 16px !important;
    font-size: 1rem;
  }

  :deep(.v-field__prepend-inner) {
    padding-top: 16px;
  }
}

.border {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
}

:deep(.v-card) {
  transition: all 0.2s ease-in-out;
  border-radius: 12px;
  overflow: hidden;

  .v-card-item {
    padding: 24px;
  }

  .v-card-title {
    line-height: 1.4;
    font-size: 1.1rem;
  }

  .v-card-subtitle {
    line-height: 1.5;
    opacity: 0.8;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
  }
}

:deep(.v-avatar) {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
